import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NotificationsResponse,
  useGetAuthInspectQuery,
  useGetNotificationsQuery,
  useLazyGetTripsByTripIdUrlQuery,
  usePostNotificationsReadMutation,
} from '../../store/backendApi';

const MAX_READ_NOTIFICATIONS = 10;

export type Notification = NotificationsResponse[number];

export interface UseNotificationsResult {
  notifications: Notification[];
  unreadNotifications: Notification[];
  readNotifications: Notification[];
  unreadCount: number;
  hasUnread: boolean;
  isLoading: boolean;
  isError: boolean;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => void;
  getTripUrl: (tripId: string) => Promise<string | null>;
  tripUrls: Record<string, string>;
}

export function useNotifications(): UseNotificationsResult {
  // Check if user is logged in before fetching notifications
  const { data: authData, isLoading: isAuthLoading } = useGetAuthInspectQuery();
  const isLoggedIn = !!authData?.userId;

  const {
    data: notifications = [],
    isLoading: isNotificationsLoading,
    isError,
    refetch,
  } = useGetNotificationsQuery(undefined, {
    skip: !isLoggedIn,
  });

  const isLoading = isAuthLoading || (isLoggedIn && isNotificationsLoading);
  const [markAsReadMutation] = usePostNotificationsReadMutation();
  const [fetchTripUrl] = useLazyGetTripsByTripIdUrlQuery();
  const [tripUrls, setTripUrls] = useState<Record<string, string>>({});

  const { unreadNotifications, readNotifications } = useMemo(() => {
    const unread: Notification[] = [];
    const read: Notification[] = [];

    // Sort by creation date (newest first)
    const sorted = [...notifications].sort(
      (a, b) => new Date(b.creation).getTime() - new Date(a.creation).getTime(),
    );

    for (const notification of sorted) {
      if (!notification.read) {
        unread.push(notification);
      } else if (read.length < MAX_READ_NOTIFICATIONS) {
        read.push(notification);
      }
    }

    return { unreadNotifications: unread, readNotifications: read };
  }, [notifications]);

  // Prefetch URLs for all visible notifications
  useEffect(() => {
    const allNotifications = [...unreadNotifications, ...readNotifications];
    const uniqueTripIds = Array.from(new Set(allNotifications.map((n) => n.tripId)));

    uniqueTripIds.forEach(async (tripId) => {
      if (!tripUrls[tripId]) {
        try {
          const result = await fetchTripUrl(tripId).unwrap();
          if (result.url) {
            const url = result.url;
            setTripUrls((prev) => ({ ...prev, [tripId]: url }));
          }
        } catch (error) {
          // Global error handler will display toast
        }
      }
    });
  }, [unreadNotifications, readNotifications, fetchTripUrl, tripUrls]);

  const getTripUrl = useCallback(
    async (tripId: string): Promise<string | null> => {
      if (tripUrls[tripId]) {
        return tripUrls[tripId];
      }
      try {
        const result = await fetchTripUrl(tripId).unwrap();
        if (result.url) {
          const url = result.url;
          setTripUrls((prev) => ({ ...prev, [tripId]: url }));
          return result.url;
        }
        return null;
      } catch (error) {
        // Global error handler will display toast
        return null;
      }
    },
    [fetchTripUrl, tripUrls],
  );

  const markAsRead = useCallback(
    async (notificationIds: string[]) => {
      if (notificationIds.length === 0) return;
      await markAsReadMutation({ notificationIds });
    },
    [markAsReadMutation],
  );

  const markAllAsRead = useCallback(async () => {
    const unreadIds = unreadNotifications.map((n) => n.id);
    await markAsRead(unreadIds);
  }, [unreadNotifications, markAsRead]);

  return {
    notifications,
    unreadNotifications,
    readNotifications,
    unreadCount: unreadNotifications.length,
    hasUnread: unreadNotifications.length > 0,
    isLoading,
    isError,
    markAsRead,
    markAllAsRead,
    refetch,
    getTripUrl,
    tripUrls,
  };
}
